import { useEffect, useState } from 'react';
import { billingGet } from '../../api/billingClient';
import { useBilling } from '../../context/BillingContext';
import { useI18n } from '../../i18n/useI18n';
import { useUi } from '../../context/UiContext';
import styles from './overlays.module.css';

const FEATURES = ['pro.reminders', 'pro.pages', 'pro.themes'];

function money(cents, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
  }
}

export function ProModal() {
  const billing = useBilling();
  const { t } = useI18n();
  const { close } = useUi();
  const [plans, setPlans] = useState([]);
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancel = false;
    billingGet('/plans').then((result) => {
      if (!cancel && result.ok) setPlans(result.body.plans || []);
    });
    return () => {
      cancel = true;
    };
  }, []);

  const subscribe = async (planId) => {
    setError('');
    setPending(planId);
    const result = await billing?.startCheckout(planId);
    if (!result?.ok) {
      setPending('');
      setError(result?.body?.error === 'already_subscribed' ? 'already' : 'unavailable');
    }
  };

  const manage = async () => {
    setError('');
    setPending('portal');
    const result = await billing?.openPortal();
    if (!result?.ok) {
      setPending('');
      setError('unavailable');
    }
  };

  const current = billing?.pro;

  return (
    <div className={styles.scrim} onClick={() => close('proOpen')}>
      <div
        className={`${styles.modal} ${styles.proModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-title"
        onClick={(event) => event.stopPropagation()}
      >
        <p className={styles.kicker}>{t('pro.kicker')}</p>
        <h2 id="pro-title">{t('pro.title')}</h2>
        <p className={styles.lede}>{t('pro.lede')}</p>
        <ul className={styles.features}>
          {FEATURES.map((feature) => (
            <li key={feature}>{t(feature)}</li>
          ))}
        </ul>
        {current ? (
          <div className={styles.currentPlan}>
            <span>{t('pro.current')}</span>
            <strong>{current.planName}</strong>
            <em>{money(current.amountCents, current.currency)} · {current.interval === 'year' ? t('pro.perYear') : t('pro.perMonth')}</em>
          </div>
        ) : (
          <div className={styles.planList}>
            {plans.map((plan) => (
              <button
                key={plan.id}
                type="button"
                className={styles.planChoice}
                disabled={Boolean(pending)}
                onClick={() => subscribe(plan.id)}
              >
                <span>
                  <strong>{plan.name}</strong>
                  <em>{money(plan.amountCents, plan.currency)} · {plan.interval === 'year' ? t('pro.perYear') : t('pro.perMonth')}</em>
                </span>
                <b>{pending === plan.id ? t('pro.opening') : t('pro.subscribe')}</b>
              </button>
            ))}
          </div>
        )}
        {error ? <p className={styles.formError}>{t(`pro.${error}`)}</p> : null}
        <div className={styles.modalActions}>
          <button type="button" className={styles.quiet} onClick={() => close('proOpen')}>
            {t('pro.later')}
          </button>
          {current ? (
            <button type="button" className={styles.primary} disabled={Boolean(pending)} onClick={manage}>
              {pending === 'portal' ? t('pro.opening') : t('pro.manage')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
